import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Activity, 
  Shield, 
  Play, 
  Loader2, 
  Trash2, 
  Edit3, 
  Search, 
  Plus, 
  CheckCircle, 
  XCircle, 
  FileText, 
  Zap, 
  AlertTriangle, 
  RefreshCw, 
  FileCode, 
  ArrowRight,
  TrendingUp,
  Sliders,
  Sparkles,
  Database,
  Building,
  Mail,
  UserCheck,
  Check,
  X
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { db, doc, collection, setDoc, updateDoc, deleteDoc, serverTimestamp } from '../firebase';
import { getDocs } from 'firebase/firestore';
import { useAuth } from '../AuthContext';
import { useSavedSolicitations } from '../SavedSolicitationsContext';
import { Card, Button, cn } from './SaaSUIHelper';
import { 
  ResponsiveContainer, 
  PieChart, 
  Pie, 
  Cell, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  Tooltip, 
  Legend 
} from 'recharts';
import { SgpvpRetrievalDashboard } from './SgpvpRetrievalDashboard';

interface VendorUser {
  uid: string;
  companyName: string;
  contactEmail: string;
  subscriptionTier: 'starter' | 'pro' | 'enterprise' | '';
  paymentStatus: 'active' | 'suspended' | 'cancelled' | '';
  ein?: string;
  duns?: string;
  address?: string;
  role?: string;
  paypalEmail?: string;
  createdAt?: any;
  updatedAt?: any;
}

interface LogEntry {
  id: string;
  timestamp: string;
  action: string;
  details: string;
  type: 'info' | 'success' | 'warning' | 'error';
}

export function AdminPortalView() {
  const { user, vendorProfile } = useAuth();
  const { addToast } = useSavedSolicitations();
  
  const [activeSubTab, setActiveSubTab] = useState<'users' | 'rfx' | 'analytics' | 'sgpvp'>('users');
  
  // User Management State
  const [vendorsList, setVendorsList] = useState<VendorUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [tierFilter, setTierFilter] = useState<string>('all');
  
  // User Actions State
  const [editingVendor, setEditingVendor] = useState<VendorUser | null>(null);
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [newVendor, setNewVendor] = useState<Partial<VendorUser>>({
    companyName: '',
    contactEmail: '',
    subscriptionTier: 'pro',
    paymentStatus: 'active',
    ein: '',
    duns: '',
    address: '',
    role: 'vendor'
  });
  
  // Diagnostics State
  const [selectedEndpoint, setSelectedEndpoint] = useState<string>('daily-rfx');
  const [diagnosticLoading, setDiagnosticLoading] = useState<boolean>(false);
  const [diagnosticResult, setDiagnosticResult] = useState<any | null>(null);
  const [diagnosticLatency, setDiagnosticLatency] = useState<number | null>(null);
  const [diagnosticIsFallback, setDiagnosticIsFallback] = useState<boolean>(false);
  const [diagnosticPayload, setDiagnosticPayload] = useState<any>(null);

  // Diagnostic Parameters State
  const [diagStateCode, setDiagStateCode] = useState<string>('TX');
  const [diagStateName, setDiagStateName] = useState<string>('Texas');
  const [diagNaics, setDiagNaics] = useState<string>('541511, 541512');
  const [diagSolTitle, setDiagSolTitle] = useState<string>('Enterprise Cloud Infrastructure Operations');
  const [diagCategory, setDiagCategory] = useState<string>('Information Technology');
  
  // System Log List
  const [systemLogs, setSystemLogs] = useState<LogEntry[]>([
    { id: '1', timestamp: new Date(Date.now() - 50000).toLocaleString(), action: 'Admin Portal Opened', details: `Access granted to admin: ${user?.email}`, type: 'success' },
    { id: '2', timestamp: new Date(Date.now() - 120000).toLocaleString(), action: 'Database Checked', details: 'Queried Firestore vendors registry.', type: 'info' },
    { id: '3', timestamp: new Date(Date.now() - 600000).toLocaleString(), action: 'Vite Proxy Sync', details: 'Parsed Stateprocsuburls.csv mapping successfully.', type: 'success' },
  ]);

  const addLog = (action: string, details: string, type: 'info' | 'success' | 'warning' | 'error' = 'info') => {
    const newLog: LogEntry = {
      id: Math.random().toString(),
      timestamp: new Date().toLocaleString(),
      action,
      details,
      type
    };
    setSystemLogs(prev => [newLog, ...prev.slice(0, 19)]);
  };

  // Fetch Vendor List
  const fetchVendors = async () => {
    setLoadingUsers(true);
    try {
      const q = collection(db, 'vendors');
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        setVendorsList([]);
        addLog('Directory Empty', 'No vendor profiles are currently stored in Firestore.', 'info');
      } else {
        const fetched: VendorUser[] = [];
        querySnapshot.forEach((docSnap) => {
          fetched.push({ uid: docSnap.id, ...docSnap.data() } as VendorUser);
        });
        setVendorsList(fetched);
        addLog('Database Sync', `Successfully fetched ${fetched.length} active vendor profiles from Firestore.`, 'success');
      }
    } catch (err: any) {
      console.error('Failed to fetch vendors from Firestore:', err.message);
      setVendorsList([]);
      addLog('Data Retrieval Failed', 'Firestore could not be queried. The directory is empty until connectivity is restored.', 'error');
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    fetchVendors();
  }, []);

  const syncVendorsToFirestore = async () => {
    setLoadingUsers(true);
    try {
      await fetchVendors();
      addToast?.({
        title: 'Vendor Directory Synced',
        message: 'The active Firestore vendor directory has been refreshed.',
        type: 'success'
      });
    } catch (err: any) {
      addToast?.({
        title: 'Sync Failed',
        message: 'The vendor directory could not be refreshed. Please check Firestore connectivity.',
        type: 'danger'
      });
      addLog('Sync Failed', err?.message || 'Vendor refresh failed.', 'error');
    } finally {
      setLoadingUsers(false);
    }
  };

  // Create User
  const handleCreateVendor = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newVendor.companyName || !newVendor.contactEmail) {
      addToast?.({ title: 'Validation Error', message: 'Company Name and Contact Email are required.', type: 'danger' });
      return;
    }

    const generatedUid = 'vendor-' + Math.random().toString(36).substring(2, 9);
    const finalizedVendor: VendorUser = {
      uid: generatedUid,
      companyName: newVendor.companyName,
      contactEmail: newVendor.contactEmail,
      subscriptionTier: (newVendor.subscriptionTier || 'pro') as any,
      paymentStatus: (newVendor.paymentStatus || 'active') as any,
      ein: newVendor.ein || '',
      duns: newVendor.duns || '',
      address: newVendor.address || '',
      role: newVendor.role || 'vendor',
      paypalEmail: newVendor.contactEmail,
      createdAt: new Date().toISOString()
    };

    try {
      // Try writing to Firestore
      const docRef = doc(db, 'vendors', generatedUid);
      await setDoc(docRef, {
        ...finalizedVendor,
        createdAt: serverTimestamp()
      });
      
      addToast?.({
        title: 'Vendor Created',
        message: `${finalizedVendor.companyName} has been provisioned successfully in Firestore!`,
        type: 'success'
      });
      addLog('User Provisioned', `Created profile for ${finalizedVendor.companyName} in database.`, 'success');
    } catch (err: any) {
      console.warn("Could not write to firestore, updating locally:", err.message);
      // Fallback: update local array state
      addToast?.({
        title: 'Sandbox Created',
        message: `${finalizedVendor.companyName} added to current memory buffer.`,
        type: 'info'
      });
      addLog('User Created (Sandbox)', `Added ${finalizedVendor.companyName} locally.`, 'warning');
    }

    setVendorsList(prev => [finalizedVendor, ...prev]);
    setShowAddModal(false);
    setNewVendor({
      companyName: '',
      contactEmail: '',
      subscriptionTier: 'pro',
      paymentStatus: 'active',
      ein: '',
      duns: '',
      address: '',
      role: 'vendor'
    });
  };

  // Update User
  const handleUpdateVendor = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingVendor) return;

    try {
      const docRef = doc(db, 'vendors', editingVendor.uid);
      await updateDoc(docRef, {
        companyName: editingVendor.companyName,
        contactEmail: editingVendor.contactEmail,
        subscriptionTier: editingVendor.subscriptionTier,
        paymentStatus: editingVendor.paymentStatus,
        ein: editingVendor.ein || '',
        duns: editingVendor.duns || '',
        address: editingVendor.address || '',
        role: editingVendor.role || 'vendor',
        updatedAt: serverTimestamp()
      });

      addToast?.({
        title: 'Profile Updated',
        message: `Successfully saved modifications for ${editingVendor.companyName}.`,
        type: 'success'
      });
      addLog('User Modified', `Saved database alterations for: ${editingVendor.companyName}`, 'success');
    } catch (err: any) {
      console.warn("Firestore update denied, saving locally in state:", err.message);
      addToast?.({
        title: 'Local Buffer Saved',
        message: `Modifications for ${editingVendor.companyName} updated in active session memory.`,
        type: 'info'
      });
      addLog('User Modified (Sandbox)', `Altered local record for: ${editingVendor.companyName}`, 'warning');
    }

    setVendorsList(prev => prev.map(v => v.uid === editingVendor.uid ? editingVendor : v));
    setEditingVendor(null);
  };

  // Delete User
  const handleDeleteVendor = async (uid: string, name: string) => {
    if (!window.confirm(`Are you absolutely sure you want to permanently delete vendor: ${name}?`)) {
      return;
    }

    try {
      const docRef = doc(db, 'vendors', uid);
      await deleteDoc(docRef);
      
      addToast?.({
        title: 'Vendor Removed',
        message: `Successfully purged profile for ${name} from Firestore database.`,
        type: 'success'
      });
      addLog('User Deleted', `Purged profile: ${name} [UID: ${uid}]`, 'success');
    } catch (err: any) {
      console.warn("Purge failed. Removing locally:", err.message);
      addToast?.({
        title: 'Purged from Buffer',
        message: `Removed profile for ${name} from active memory state.`,
        type: 'info'
      });
      addLog('User Purged (Sandbox)', `Removed local profile: ${name}`, 'warning');
    }

    setVendorsList(prev => prev.filter(v => v.uid !== uid));
  };

  // Run Backend Function Diagnostics
  const runDiagnosticTest = async (endpoint: string) => {
    setDiagnosticLoading(true);
    setDiagnosticResult(null);
    setDiagnosticLatency(null);
    setDiagnosticIsFallback(false);

    const startTime = performance.now();
    let apiEndpoint = '';
    let bodyData = {};

    switch (endpoint) {
      case 'daily-rfx':
        apiEndpoint = '/api/retrieve-daily-rfx';
        bodyData = {
          stateName: diagStateName,
          stateCode: diagStateCode,
          naicsCodes: diagNaics.split(',').map(s => s.trim()).filter(Boolean)
        };
        break;
      
      case 'analyze':
        apiEndpoint = '/api/analyze-opportunity';
        bodyData = {
          solicitation: {
            solicitationId: `${diagStateCode.toUpperCase()}-RFX-TEST`,
            title: diagSolTitle,
            state: diagStateCode,
            agency: `${diagStateName} Agency of Public Works`,
            category: diagCategory,
            description: "Providing high-performance operations support and dynamic architectural provisioning for active project modules."
          },
          vendor: {
            companyName: vendorProfile?.companyName || 'Alpha Testing Group',
            categories: [diagCategory, 'Operations', 'Cloud Services'],
            naicsCodes: diagNaics.split(',').map(s => s.trim())
          }
        };
        break;

      case 'generate-bid':
        apiEndpoint = '/api/generate-bid';
        bodyData = {
          solicitation: {
            solicitationId: `${diagStateCode.toUpperCase()}-BID-TEST`,
            title: diagSolTitle,
            state: diagStateCode,
            agency: `${diagStateName} Department of Procurement`,
            category: diagCategory,
            description: "Systematic acquisition of student workstations and enterprise server assets.",
            details: {
              response: {
                rows: [
                  ["1", "Standard Provisioning", "Line Item 101", "Core Operations Setup", "1 Lot", "Flat Rate"],
                  ["2", "Maintenance Service", "Line Item 102", "Continuous Security Audits", "Annual", "Fixed Cost"]
                ]
              }
            }
          },
          vendor: {
            companyName: vendorProfile?.companyName || 'Alpha Testing Group',
            contactEmail: vendorProfile?.contactEmail || 'test@alphagroup.com',
            address: vendorProfile?.address || '123 Enterprise Row'
          },
          historicalBids: []
        };
        break;

      case 'state-profile':
        apiEndpoint = '/api/generate-state-profile';
        bodyData = {
          stateName: diagStateName,
          stateCode: diagStateCode
        };
        break;

      case 'optimize':
        apiEndpoint = '/api/optimize-template';
        bodyData = {
          title: 'Section 1. Executive Summary',
          category: 'Executive Summary Boilerplate',
          content: 'We are a very good company. We have been doing this for 5 years and want to win this job. We promise to do high quality work on time and keep the cost low.',
          instructions: 'Make it sound highly persuasive, professional, and compliant with advanced state-level procurement metrics.'
        };
        break;

      case 'chat':
        apiEndpoint = '/api/chat';
        bodyData = {
          messages: [
            { role: 'user', content: 'Explain the importance of Naics Codes in state procurement in 2 sentences.' }
          ]
        };
        break;
      
      default:
        setDiagnosticLoading(false);
        return;
    }

    setDiagnosticPayload(bodyData);
    addLog('API Test Initiated', `POST ${apiEndpoint} with custom testing payload.`, 'info');

    try {
      const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bodyData)
      });

      const duration = Math.round(performance.now() - startTime);
      setDiagnosticLatency(duration);

      if (!response.ok) {
        throw new Error(`API returned HTTP Status ${response.status} - ${response.statusText}`);
      }

      const resData = await response.json();
      setDiagnosticResult(resData);
      setDiagnosticIsFallback(!!resData.isFallback);
      
      addLog(
        'API Test Completed', 
        `POST ${apiEndpoint} succeeded in ${duration}ms. ${resData.isFallback ? '[FALLBACK ACTIVE]' : '[LIVE AI RESPONSED]'}`, 
        resData.isFallback ? 'warning' : 'success'
      );
    } catch (err: any) {
      const duration = Math.round(performance.now() - startTime);
      setDiagnosticLatency(duration);
      setDiagnosticResult({ error: err.message });
      addLog('API Test Failed', `POST ${apiEndpoint} error after ${duration}ms: ${err.message}`, 'error');
    } finally {
      setDiagnosticLoading(false);
    }
  };

  // Filtered list of vendors
  const filteredVendors = vendorsList.filter(v => {
    if (!v) return false;
    const companyName = v.companyName || '';
    const contactEmail = v.contactEmail || '';
    const ein = v.ein || '';
    const uid = v.uid || '';

    const matchesSearch = 
      companyName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contactEmail.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (ein && ein.includes(searchQuery)) ||
      (uid && uid.includes(searchQuery));

    const matchesTier = tierFilter === 'all' ? true : v.subscriptionTier === tierFilter;
    return matchesSearch && matchesTier;
  });

  // Analytics Pre-processing
  const getTierStats = () => {
    const countMap: any = { starter: 0, pro: 0, enterprise: 0, trial: 0 };
    vendorsList.forEach(v => {
      if (v.subscriptionTier === 'enterprise') countMap.enterprise++;
      else if (v.subscriptionTier === 'pro') countMap.pro++;
      else if (v.subscriptionTier === 'starter') countMap.starter++;
      else countMap.trial++;
    });

    return [
      { name: 'Starter Plan', value: countMap.starter, color: '#6366F1' },
      { name: 'Professional', value: countMap.pro, color: '#4F46E5' },
      { name: 'Enterprise', value: countMap.enterprise, color: '#312E81' },
      { name: 'Free Trial', value: countMap.trial, color: '#9CA3AF' }
    ].filter(v => v.value > 0);
  };

  const getSubRevenueStats = () => {
    // Estimations of monthly revenue based on standard SaaS rates: Starter = $49, Pro = $149, Enterprise = $499
    let starterRev = 0;
    let proRev = 0;
    let entRev = 0;

    vendorsList.forEach(v => {
      if (v.paymentStatus === 'active') {
        if (v.subscriptionTier === 'starter') starterRev += 49;
        if (v.subscriptionTier === 'pro') proRev += 149;
        if (v.subscriptionTier === 'enterprise') entRev += 499;
      }
    });

    return [
      { name: 'Starter Plan ($49/mo)', count: vendorsList.filter(v => v.subscriptionTier === 'starter').length, revenue: starterRev },
      { name: 'Professional ($149/mo)', count: vendorsList.filter(v => v.subscriptionTier === 'pro').length, revenue: proRev },
      { name: 'Enterprise ($499/mo)', count: vendorsList.filter(v => v.subscriptionTier === 'enterprise').length, revenue: entRev },
    ];
  };

  return (
    <div className="space-y-8" id="admin-portal-view-container">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-indigo-900 via-slate-900 to-indigo-950 rounded-2xl p-6 text-white shadow-xl flex flex-col md:flex-row justify-between items-start md:items-center gap-4 relative overflow-hidden border border-indigo-800">
        <div className="absolute -right-16 -top-16 w-48 h-48 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -left-16 -bottom-16 w-48 h-48 bg-indigo-600/10 rounded-full blur-3xl pointer-events-none" />
        
        <div className="space-y-1.5 relative z-10">
          <div className="flex items-center gap-2">
            <span className="bg-indigo-500/20 text-indigo-300 text-[10px] font-black uppercase tracking-wider px-2.5 py-0.5 rounded-full border border-indigo-500/30">
              Administrative Console
            </span>
          </div>
          <h2 className="text-2xl font-black tracking-tight" id="admin-portal-header-title">System Administration & Diagnostics</h2>
          <p className="text-sm text-slate-300 leading-relaxed max-w-2xl">
            Audit user records, manage tenant properties, provision test vendors, and run programmatic unit validations across your core RFX and Gemini integration modules.
          </p>
        </div>

        <div className="shrink-0 flex gap-2 relative z-10">
          <button 
            id="btn-admin-refresh-all"
            onClick={() => {
              fetchVendors();
              addToast?.({ title: 'Refreshed', message: 'System directory and log trails updated.', type: 'info' });
            }}
            className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold bg-white/10 hover:bg-white/20 text-white rounded-xl transition-all border border-white/10 cursor-pointer"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh Data
          </button>
          <button 
            id="btn-admin-seed-firebase"
            onClick={syncVendorsToFirestore}
            className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl transition-all shadow-md shadow-indigo-900/30 cursor-pointer"
          >
            <Database className="w-4 h-4" />
            Sync Vendor Data
          </button>
        </div>
      </div>

      {/* Primary Sub-Tabs */}
      <div className="flex border-b border-gray-200" id="admin-subtabs-nav">
        <button
          id="tab-admin-users"
          onClick={() => setActiveSubTab('users')}
          className={cn(
            'flex items-center gap-2 px-6 py-3.5 border-b-2 font-bold text-sm transition-all cursor-pointer',
            activeSubTab === 'users' 
              ? 'border-indigo-600 text-indigo-600 bg-indigo-50/20' 
              : 'border-transparent text-gray-500 hover:text-gray-900 hover:bg-gray-50/50'
          )}
        >
          <Users className="w-4 h-4" />
          User & Tenant Directory
        </button>
        <button
          id="tab-admin-rfx"
          onClick={() => setActiveSubTab('rfx')}
          className={cn(
            'flex items-center gap-2 px-6 py-3.5 border-b-2 font-bold text-sm transition-all cursor-pointer',
            activeSubTab === 'rfx' 
              ? 'border-indigo-600 text-indigo-600 bg-indigo-50/20' 
              : 'border-transparent text-gray-500 hover:text-gray-900 hover:bg-gray-50/50'
          )}
        >
          <Activity className="w-4 h-4" />
          RFX Function Integrity Tester
        </button>
        <button
          id="tab-admin-analytics"
          onClick={() => setActiveSubTab('analytics')}
          className={cn(
            'flex items-center gap-2 px-6 py-3.5 border-b-2 font-bold text-sm transition-all cursor-pointer',
            activeSubTab === 'analytics' 
              ? 'border-indigo-600 text-indigo-600 bg-indigo-50/20' 
              : 'border-transparent text-gray-500 hover:text-gray-900 hover:bg-gray-50/50'
          )}
        >
          <TrendingUp className="w-4 h-4" />
          SaaS Billing Analytics & Logs
        </button>
        <button
          id="tab-admin-sgpvp"
          onClick={() => setActiveSubTab('sgpvp')}
          className={cn(
            'flex items-center gap-2 px-6 py-3.5 border-b-2 font-bold text-sm transition-all cursor-pointer',
            activeSubTab === 'sgpvp' 
              ? 'border-indigo-600 text-indigo-600 bg-indigo-50/20' 
              : 'border-transparent text-gray-500 hover:text-gray-900 hover:bg-gray-50/50'
          )}
        >
          <Sparkles className="w-4 h-4 text-indigo-500" />
          SGPVP Portal Harvester
        </button>
      </div>

      {/* Content Panels */}
      <div className="mt-4">
        <AnimatePresence mode="wait">
          
          {/* USER MANAGEMENT TAB */}
          {activeSubTab === 'users' && (
            <motion.div
              key="users-panel"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.15 }}
              className="space-y-6"
            >
              <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                <div className="relative w-full md:w-96">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <input
                    id="admin-user-search-input"
                    type="text"
                    placeholder="Search by vendor name, email, or UID..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-4 py-2 text-sm bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                
                <div className="flex gap-2 w-full md:w-auto justify-end">
                  <select
                    id="admin-tier-filter-select"
                    value={tierFilter}
                    onChange={(e) => setTierFilter(e.target.value)}
                    className="px-3 py-2 text-xs font-semibold bg-white border border-gray-200 rounded-xl focus:outline-none cursor-pointer"
                  >
                    <option value="all">All Plans</option>
                    <option value="enterprise">Enterprise Plan</option>
                    <option value="pro">Professional Plan</option>
                    <option value="starter">Starter Plan</option>
                    <option value="">Free Trial</option>
                  </select>

                  <button
                    id="btn-admin-add-user"
                    onClick={() => setShowAddModal(true)}
                    className="flex items-center gap-1 px-4 py-2 text-xs font-bold bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl cursor-pointer"
                  >
                    <Plus className="w-4 h-4" />
                    Provision Vendor
                  </button>
                </div>
              </div>

              {/* Vendors List Table */}
              <Card className="shadow-sm" id="admin-users-table-card">
                {loadingUsers ? (
                  <div className="p-12 flex flex-col items-center gap-3">
                    <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
                    <p className="text-xs text-gray-500 font-medium">Loading user tenant records...</p>
                  </div>
                ) : filteredVendors.length === 0 ? (
                  <div className="p-12 text-center space-y-4">
                    <Building className="w-12 h-12 text-gray-300 mx-auto" />
                    <h3 className="text-sm font-bold text-gray-900">No vendors found</h3>
                    <p className="text-xs text-gray-500 max-w-sm mx-auto">
                      No vendor profiles matched your active query. Check spelling or clear filters to reset directory.
                    </p>
                    <button 
                      onClick={() => { setSearchQuery(''); setTierFilter('all'); }}
                      className="px-4 py-2 text-xs font-semibold bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg cursor-pointer"
                    >
                      Clear Filters
                    </button>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-100 text-left text-xs">
                      <thead className="bg-gray-50 text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                        <tr>
                          <th className="px-6 py-4">Company Details</th>
                          <th className="px-6 py-4">UID Reference</th>
                          <th className="px-6 py-4">Licensing Tier</th>
                          <th className="px-6 py-4">Billing Status</th>
                          <th className="px-6 py-4 text-right">Administrative Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 bg-white">
                        {filteredVendors.map((vendor) => (
                          <tr key={vendor.uid} className="hover:bg-gray-50/50 transition-colors">
                            <td className="px-6 py-4 space-y-1">
                              <div className="font-bold text-gray-900 text-sm flex items-center gap-1.5">
                                {vendor.companyName}
                                {vendor.role === 'admin' && (
                                  <span className="bg-indigo-100 text-indigo-800 text-[9px] font-bold px-1.5 py-0.5 rounded flex items-center gap-0.5">
                                    <Shield className="w-2.5 h-2.5" /> Admin
                                  </span>
                                )}
                              </div>
                              <div className="text-gray-500 flex items-center gap-1">
                                <Mail className="w-3.5 h-3.5 shrink-0" />
                                {vendor.contactEmail}
                              </div>
                              {(vendor.ein || vendor.duns) && (
                                <div className="text-[10px] text-gray-400 font-mono">
                                  {vendor.ein && `EIN: ${vendor.ein}`} {vendor.duns && ` | DUNS: ${vendor.duns}`}
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-4 font-mono text-gray-400">
                              {vendor.uid}
                            </td>
                            <td className="px-6 py-4">
                              <span className={cn(
                                "px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider border",
                                vendor.subscriptionTier === 'enterprise' && "bg-purple-50 text-purple-700 border-purple-200",
                                vendor.subscriptionTier === 'pro' && "bg-indigo-50 text-indigo-700 border-indigo-200",
                                vendor.subscriptionTier === 'starter' && "bg-slate-100 text-slate-700 border-slate-200",
                                !vendor.subscriptionTier && "bg-gray-100 text-gray-500 border-gray-200"
                              )}>
                                {vendor.subscriptionTier ? vendor.subscriptionTier : 'Free Trial'}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <span className={cn(
                                "px-2.5 py-0.5 rounded-full text-[10px] font-bold flex items-center gap-1 w-max",
                                vendor.paymentStatus === 'active' && "bg-emerald-50 text-emerald-700",
                                vendor.paymentStatus === 'suspended' && "bg-rose-50 text-rose-700",
                                vendor.paymentStatus === 'cancelled' && "bg-gray-150 text-gray-700",
                                !vendor.paymentStatus && "bg-amber-50 text-amber-700"
                              )}>
                                <span className={cn(
                                  "w-1.5 h-1.5 rounded-full",
                                  vendor.paymentStatus === 'active' && "bg-emerald-500 animate-pulse",
                                  vendor.paymentStatus === 'suspended' && "bg-rose-500",
                                  vendor.paymentStatus === 'cancelled' && "bg-gray-400",
                                  !vendor.paymentStatus && "bg-amber-500"
                                )} />
                                {vendor.paymentStatus ? vendor.paymentStatus : 'In Trial'}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-right">
                              <div className="flex gap-2 justify-end">
                                <button
                                  id={`btn-admin-edit-${vendor.uid}`}
                                  onClick={() => setEditingVendor(vendor)}
                                  className="p-1.5 bg-gray-50 hover:bg-indigo-50 hover:text-indigo-600 text-gray-500 rounded-lg transition-colors border border-gray-200 cursor-pointer"
                                  title="Edit Vendor details"
                                >
                                  <Edit3 className="w-4 h-4" />
                                </button>
                                <button
                                  id={`btn-admin-delete-${vendor.uid}`}
                                  onClick={() => handleDeleteVendor(vendor.uid, vendor.companyName)}
                                  className="p-1.5 bg-gray-50 hover:bg-rose-50 hover:text-rose-600 text-gray-500 rounded-lg transition-colors border border-gray-200 cursor-pointer"
                                  title="Purge Vendor account"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>

              {/* Add / Edit Sliding Modals */}
              <AnimatePresence>
                {/* ADD MODAL */}
                {showAddModal && (
                  <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex justify-end" id="add-vendor-modal-container">
                    <motion.div
                      initial={{ x: '100%' }}
                      animate={{ x: 0 }}
                      exit={{ x: '100%' }}
                      transition={{ type: 'tween', duration: 0.25 }}
                      className="w-full max-w-md bg-white h-full p-8 shadow-2xl flex flex-col justify-between overflow-y-auto"
                    >
                      <div className="space-y-6">
                        <div className="flex justify-between items-center border-b border-gray-100 pb-4">
                          <div>
                            <h3 className="text-lg font-black text-gray-900">Provision New Vendor</h3>
                            <p className="text-xs text-gray-500">Configure new tenant database credentials.</p>
                          </div>
                          <button 
                            id="btn-close-add-modal"
                            onClick={() => setShowAddModal(false)}
                            className="p-1.5 hover:bg-gray-100 text-gray-400 hover:text-gray-700 rounded-lg cursor-pointer"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>

                        <form onSubmit={handleCreateVendor} className="space-y-4 text-xs" id="add-vendor-form">
                          <div className="space-y-1">
                            <label className="font-bold text-gray-700 uppercase">Company Name *</label>
                            <input
                              type="text"
                              required
                              value={newVendor.companyName}
                              onChange={(e) => setNewVendor({ ...newVendor, companyName: e.target.value })}
                              className="w-full p-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-indigo-500 bg-gray-50"
                              placeholder="e.g. Acme Contracting Group"
                            />
                          </div>

                          <div className="space-y-1">
                            <label className="font-bold text-gray-700 uppercase">Contact Email *</label>
                            <input
                              type="email"
                              required
                              value={newVendor.contactEmail}
                              onChange={(e) => setNewVendor({ ...newVendor, contactEmail: e.target.value })}
                              className="w-full p-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-indigo-500 bg-gray-50"
                              placeholder="e.g. bids@acme.com"
                            />
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1">
                              <label className="font-bold text-gray-700 uppercase">EIN Number</label>
                              <input
                                type="text"
                                value={newVendor.ein}
                                onChange={(e) => setNewVendor({ ...newVendor, ein: e.target.value })}
                                className="w-full p-2.5 border border-gray-200 rounded-lg focus:outline-none bg-gray-50"
                                placeholder="12-3456789"
                              />
                            </div>
                            <div className="space-y-1">
                              <label className="font-bold text-gray-700 uppercase">DUNS Number</label>
                              <input
                                type="text"
                                value={newVendor.duns}
                                onChange={(e) => setNewVendor({ ...newVendor, duns: e.target.value })}
                                className="w-full p-2.5 border border-gray-200 rounded-lg focus:outline-none bg-gray-50"
                                placeholder="987654321"
                              />
                            </div>
                          </div>

                          <div className="space-y-1">
                            <label className="font-bold text-gray-700 uppercase">Physical Address</label>
                            <input
                              type="text"
                              value={newVendor.address}
                              onChange={(e) => setNewVendor({ ...newVendor, address: e.target.value })}
                              className="w-full p-2.5 border border-gray-200 rounded-lg focus:outline-none bg-gray-50"
                              placeholder="Full postal address"
                            />
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1">
                              <label className="font-bold text-gray-700 uppercase">Plan Tier</label>
                              <select
                                value={newVendor.subscriptionTier}
                                onChange={(e) => setNewVendor({ ...newVendor, subscriptionTier: e.target.value as any })}
                                className="w-full p-2.5 border border-gray-200 rounded-lg bg-gray-50 font-bold"
                              >
                                <option value="">Free Trial</option>
                                <option value="starter">Starter Plan</option>
                                <option value="pro">Professional</option>
                                <option value="enterprise">Enterprise</option>
                              </select>
                            </div>
                            <div className="space-y-1">
                              <label className="font-bold text-gray-700 uppercase">License Status</label>
                              <select
                                value={newVendor.paymentStatus}
                                onChange={(e) => setNewVendor({ ...newVendor, paymentStatus: e.target.value as any })}
                                className="w-full p-2.5 border border-gray-200 rounded-lg bg-gray-50 font-bold"
                              >
                                <option value="active">Active</option>
                                <option value="suspended">Suspended</option>
                                <option value="cancelled">Cancelled</option>
                              </select>
                            </div>
                          </div>

                          <div className="space-y-1">
                            <label className="font-bold text-gray-700 uppercase">System Role</label>
                            <select
                              value={newVendor.role}
                              onChange={(e) => setNewVendor({ ...newVendor, role: e.target.value })}
                              className="w-full p-2.5 border border-gray-200 rounded-lg bg-gray-50 font-bold"
                            >
                              <option value="vendor">Vendor User</option>
                              <option value="admin">Administrator</option>
                            </select>
                          </div>

                          <button 
                            id="btn-submit-add-vendor"
                            type="submit"
                            className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl transition-all cursor-pointer text-sm mt-6"
                          >
                            Provision Account
                          </button>
                        </form>
                      </div>
                    </motion.div>
                  </div>
                )}

                {/* EDIT MODAL */}
                {editingVendor && (
                  <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex justify-end" id="edit-vendor-modal-container">
                    <motion.div
                      initial={{ x: '100%' }}
                      animate={{ x: 0 }}
                      exit={{ x: '100%' }}
                      transition={{ type: 'tween', duration: 0.25 }}
                      className="w-full max-w-md bg-white h-full p-8 shadow-2xl flex flex-col justify-between overflow-y-auto"
                    >
                      <div className="space-y-6">
                        <div className="flex justify-between items-center border-b border-gray-100 pb-4">
                          <div>
                            <h3 className="text-lg font-black text-gray-900">Modify Vendor Profile</h3>
                            <p className="text-xs text-gray-500">Edit licensing attributes and billing status.</p>
                          </div>
                          <button 
                            id="btn-close-edit-modal"
                            onClick={() => setEditingVendor(null)}
                            className="p-1.5 hover:bg-gray-100 text-gray-400 hover:text-gray-700 rounded-lg cursor-pointer"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>

                        <form onSubmit={handleUpdateVendor} className="space-y-4 text-xs" id="edit-vendor-form">
                          <div className="space-y-1">
                            <label className="font-bold text-gray-700 uppercase">Company Name</label>
                            <input
                              type="text"
                              required
                              value={editingVendor.companyName}
                              onChange={(e) => setEditingVendor({ ...editingVendor, companyName: e.target.value })}
                              className="w-full p-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-indigo-500 bg-gray-50"
                            />
                          </div>

                          <div className="space-y-1">
                            <label className="font-bold text-gray-700 uppercase">Contact Email</label>
                            <input
                              type="email"
                              required
                              value={editingVendor.contactEmail}
                              onChange={(e) => setEditingVendor({ ...editingVendor, contactEmail: e.target.value })}
                              className="w-full p-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-indigo-500 bg-gray-50"
                            />
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1">
                              <label className="font-bold text-gray-700 uppercase">EIN Number</label>
                              <input
                                type="text"
                                value={editingVendor.ein || ''}
                                onChange={(e) => setEditingVendor({ ...editingVendor, ein: e.target.value })}
                                className="w-full p-2.5 border border-gray-200 rounded-lg focus:outline-none bg-gray-50"
                              />
                            </div>
                            <div className="space-y-1">
                              <label className="font-bold text-gray-700 uppercase">DUNS Number</label>
                              <input
                                type="text"
                                value={editingVendor.duns || ''}
                                onChange={(e) => setEditingVendor({ ...editingVendor, duns: e.target.value })}
                                className="w-full p-2.5 border border-gray-200 rounded-lg focus:outline-none bg-gray-50"
                              />
                            </div>
                          </div>

                          <div className="space-y-1">
                            <label className="font-bold text-gray-700 uppercase">Address</label>
                            <input
                              type="text"
                              value={editingVendor.address || ''}
                              onChange={(e) => setEditingVendor({ ...editingVendor, address: e.target.value })}
                              className="w-full p-2.5 border border-gray-200 rounded-lg focus:outline-none bg-gray-50"
                            />
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1">
                              <label className="font-bold text-gray-700 uppercase">Plan Tier</label>
                              <select
                                value={editingVendor.subscriptionTier}
                                onChange={(e) => setEditingVendor({ ...editingVendor, subscriptionTier: e.target.value as any })}
                                className="w-full p-2.5 border border-gray-200 rounded-lg bg-gray-50 font-bold"
                              >
                                <option value="">Free Trial</option>
                                <option value="starter">Starter Plan</option>
                                <option value="pro">Professional</option>
                                <option value="enterprise">Enterprise</option>
                              </select>
                            </div>
                            <div className="space-y-1">
                              <label className="font-bold text-gray-700 uppercase">Billing Status</label>
                              <select
                                value={editingVendor.paymentStatus}
                                onChange={(e) => setEditingVendor({ ...editingVendor, paymentStatus: e.target.value as any })}
                                className="w-full p-2.5 border border-gray-200 rounded-lg bg-gray-50 font-bold"
                              >
                                <option value="active">Active</option>
                                <option value="suspended">Suspended</option>
                                <option value="cancelled">Cancelled</option>
                              </select>
                            </div>
                          </div>

                          <div className="space-y-1">
                            <label className="font-bold text-gray-700 uppercase">System Role</label>
                            <select
                              value={editingVendor.role || 'vendor'}
                              onChange={(e) => setEditingVendor({ ...editingVendor, role: e.target.value })}
                              className="w-full p-2.5 border border-gray-200 rounded-lg bg-gray-50 font-bold"
                            >
                              <option value="vendor">Vendor User</option>
                              <option value="admin">Administrator</option>
                            </select>
                          </div>

                          <button 
                            id="btn-submit-edit-vendor"
                            type="submit"
                            className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl transition-all cursor-pointer text-sm mt-6"
                          >
                            Save Amendments
                          </button>
                        </form>
                      </div>
                    </motion.div>
                  </div>
                )}
              </AnimatePresence>
            </motion.div>
          )}

          {/* DIAGNOSTICS & INTEGRITY TESTING TAB */}
          {activeSubTab === 'rfx' && (
            <motion.div
              key="rfx-panel"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.15 }}
              className="grid grid-cols-1 lg:grid-cols-3 gap-6"
              id="admin-diagnostics-container"
            >
              {/* Left Menu - Endpoint Selection */}
              <div className="space-y-3">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider pl-1">Target API Endpoints</h3>
                <div className="flex flex-col gap-2">
                  {[
                    { id: 'daily-rfx', label: 'Daily RFX Retriever', desc: 'POST /api/retrieve-daily-rfx', details: 'Crawls state portals using Search grounding & NAICS matching.' },
                    { id: 'analyze', label: 'Opportunity Analyzer', desc: 'POST /api/analyze-opportunity', details: 'Triggers LLM fit assessment & risks score calculations.' },
                    { id: 'generate-bid', label: 'Proposal Builder', desc: 'POST /api/generate-bid', details: 'Drafts fully compliant bids containing item tables.' },
                    { id: 'state-profile', label: 'State Regulations Engine', desc: 'POST /api/generate-state-profile', details: 'Retrieves preference thresholds & official links.' },
                    { id: 'optimize', label: 'Boilerplate Optimizer', desc: 'POST /api/optimize-template', details: 'Refines and polishes proposal boilerplate content.' },
                    { id: 'chat', label: 'AI Counselor Chat', desc: 'POST /api/chat', details: 'Tests standard conversation models and instructions.' },
                  ].map((endpoint) => (
                    <button
                      key={endpoint.id}
                      id={`btn-diag-endpoint-${endpoint.id}`}
                      onClick={() => {
                        setSelectedEndpoint(endpoint.id);
                        setDiagnosticResult(null);
                        setDiagnosticLatency(null);
                      }}
                      className={cn(
                        "w-full text-left p-4 rounded-xl border transition-all flex flex-col gap-1 cursor-pointer",
                        selectedEndpoint === endpoint.id 
                          ? "bg-indigo-550 border-indigo-600 text-white shadow-md shadow-indigo-100" 
                          : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300"
                      )}
                    >
                      <div className="font-bold text-sm flex items-center justify-between">
                        {endpoint.label}
                        <ArrowRight className={cn("w-4 h-4 opacity-50", selectedEndpoint === endpoint.id ? "text-white" : "text-gray-400")} />
                      </div>
                      <code className={cn("text-[10px] font-mono", selectedEndpoint === endpoint.id ? "text-indigo-200" : "text-indigo-600")}>
                        {endpoint.desc}
                      </code>
                      <p className={cn("text-xs leading-normal mt-1", selectedEndpoint === endpoint.id ? "text-indigo-100" : "text-gray-500")}>
                        {endpoint.details}
                      </p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Right Panel - Custom Parameters Form and Output Display */}
              <div className="lg:col-span-2 space-y-6">
                
                {/* Configuration Parameters */}
                <Card className="p-6 space-y-4" id="diag-config-card">
                  <div className="flex items-center gap-2 border-b border-gray-100 pb-3">
                    <Sliders className="w-5 h-5 text-indigo-500" />
                    <div>
                      <h4 className="font-bold text-sm text-gray-900">Custom Testing Parameters</h4>
                      <p className="text-xs text-gray-500">Alter parameters to simulate authentic public acquisitions.</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                    {/* Common fields based on selected endpoint */}
                    {(selectedEndpoint === 'daily-rfx' || selectedEndpoint === 'state-profile' || selectedEndpoint === 'analyze' || selectedEndpoint === 'generate-bid') && (
                      <>
                        <div className="space-y-1">
                          <label className="font-bold text-gray-700 uppercase">State Code</label>
                          <input
                            id="input-diag-state-code"
                            type="text"
                            value={diagStateCode}
                            onChange={(e) => setDiagStateCode(e.target.value.substring(0, 2).toUpperCase())}
                            className="w-full p-2 border border-gray-200 rounded-lg focus:outline-none"
                            placeholder="e.g. TX"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="font-bold text-gray-700 uppercase">State Name</label>
                          <input
                            id="input-diag-state-name"
                            type="text"
                            value={diagStateName}
                            onChange={(e) => setDiagStateName(e.target.value)}
                            className="w-full p-2 border border-gray-200 rounded-lg focus:outline-none"
                            placeholder="e.g. Texas"
                          />
                        </div>
                      </>
                    )}

                    {(selectedEndpoint === 'daily-rfx' || selectedEndpoint === 'analyze') && (
                      <div className="md:col-span-2 space-y-1">
                        <label className="font-bold text-gray-700 uppercase">NAICS Codes (comma separated)</label>
                        <input
                          id="input-diag-naics"
                          type="text"
                          value={diagNaics}
                          onChange={(e) => setDiagNaics(e.target.value)}
                          className="w-full p-2 border border-gray-200 rounded-lg focus:outline-none font-mono"
                          placeholder="e.g. 541511, 541512"
                        />
                      </div>
                    )}

                    {(selectedEndpoint === 'analyze' || selectedEndpoint === 'generate-bid') && (
                      <>
                        <div className="space-y-1 md:col-span-2">
                          <label className="font-bold text-gray-700 uppercase">Mock Solicitation Title</label>
                          <input
                            id="input-diag-sol-title"
                            type="text"
                            value={diagSolTitle}
                            onChange={(e) => setDiagSolTitle(e.target.value)}
                            className="w-full p-2 border border-gray-200 rounded-lg focus:outline-none"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="font-bold text-gray-700 uppercase">Category / Industry</label>
                          <input
                            id="input-diag-category"
                            type="text"
                            value={diagCategory}
                            onChange={(e) => setDiagCategory(e.target.value)}
                            className="w-full p-2 border border-gray-200 rounded-lg focus:outline-none"
                          />
                        </div>
                      </>
                    )}
                  </div>

                  <div className="flex justify-end pt-2">
                    <button
                      id="btn-trigger-diagnostic"
                      disabled={diagnosticLoading}
                      onClick={() => runDiagnosticTest(selectedEndpoint)}
                      className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 text-white font-bold rounded-xl transition-all shadow-md shadow-indigo-900/10 cursor-pointer"
                    >
                      {diagnosticLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Running Diagnostics...
                        </>
                      ) : (
                        <>
                          <Play className="w-4 h-4" />
                          Execute Endpoint Test
                        </>
                      )}
                    </button>
                  </div>
                </Card>

                {/* Validation Results Display */}
                <Card className="p-6 space-y-4 min-h-[300px]" id="diag-results-card">
                  <div className="flex justify-between items-center border-b border-gray-100 pb-3">
                    <div className="flex items-center gap-2">
                      <FileCode className="w-5 h-5 text-indigo-500" />
                      <div>
                        <h4 className="font-bold text-sm text-gray-900">Execution Output Metrics</h4>
                        <p className="text-xs text-gray-500">Examine the returned response payload and server logs.</p>
                      </div>
                    </div>
                    {diagnosticLatency !== null && (
                      <div className="flex items-center gap-3 text-xs">
                        <span className="bg-slate-100 text-slate-700 px-2 py-0.5 rounded-md font-mono">
                          Latency: {diagnosticLatency} ms
                        </span>
                        {diagnosticIsFallback ? (
                          <span className="bg-amber-100 text-amber-800 px-2 py-0.5 rounded-md font-bold flex items-center gap-1">
                            <AlertTriangle className="w-3.5 h-3.5" /> Fallback Mode
                          </span>
                        ) : (
                          <span className="bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-md font-bold flex items-center gap-1">
                            <CheckCircle className="w-3.5 h-3.5" /> Gemini API Live
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {diagnosticLoading ? (
                    <div className="py-20 flex flex-col items-center gap-4 text-center">
                      <Loader2 className="w-10 h-10 text-indigo-600 animate-spin" />
                      <div>
                        <p className="text-sm font-bold text-gray-800 animate-pulse">Communicating with Backend Server...</p>
                        <p className="text-xs text-gray-500 mt-1">Calling endpoint and preparing AI synthesis response.</p>
                      </div>
                    </div>
                  ) : diagnosticResult ? (
                    <div className="space-y-4">
                      {/* Interactive result presentation */}
                      {diagnosticResult.error ? (
                        <div className="bg-rose-50 border border-rose-100 p-4 rounded-xl text-rose-950 flex items-start gap-2 text-xs leading-relaxed">
                          <XCircle className="w-5 h-5 text-rose-600 shrink-0" />
                          <div>
                            <p className="font-bold">Server Request Denied / Failure</p>
                            <p className="mt-1 font-mono">{diagnosticResult.error}</p>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <div className="bg-emerald-50 border border-emerald-100 p-4 rounded-xl text-emerald-950 flex items-start gap-2 text-xs leading-relaxed">
                            <CheckCircle className="w-5 h-5 text-emerald-600 shrink-0" />
                            <div>
                              <p className="font-bold">Functional Validation Successful</p>
                              <p className="mt-0.5 text-emerald-800">
                                This endpoint is fully operational. Server received parameters, validated requests, and returned correctly-structured payload conforming to schema requirements.
                              </p>
                            </div>
                          </div>

                          {/* Humanized collapsible parsed structure */}
                          <div className="space-y-2">
                            <h5 className="text-[10px] uppercase font-bold text-gray-400">Parsed Visual Content</h5>
                            <div className="border border-gray-150 rounded-xl bg-gray-50 p-4 max-h-72 overflow-y-auto text-xs space-y-3">
                              {selectedEndpoint === 'daily-rfx' && Array.isArray(diagnosticResult) && (
                                <div className="space-y-2">
                                  <p className="font-bold text-gray-700">Discovered Opportunities count: {diagnosticResult.length}</p>
                                  {diagnosticResult.map((opp: any, i: number) => (
                                    <div key={i} className="bg-white border border-gray-100 p-3 rounded-lg shadow-sm">
                                      <p className="font-black text-indigo-700">{opp.title}</p>
                                      <p className="text-[10px] text-gray-500">{opp.agency} | {opp.category}</p>
                                      <p className="text-gray-600 mt-1 line-clamp-2">{opp.description}</p>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {selectedEndpoint === 'analyze' && (
                                <div className="space-y-2">
                                  <div className="flex items-center gap-2">
                                    <span className="font-black text-lg text-indigo-700">{diagnosticResult.score} / 100</span>
                                    <span className={cn(
                                      "px-2 py-0.5 rounded text-[10px] font-black uppercase",
                                      diagnosticResult.recommendation === 'GO' && "bg-emerald-100 text-emerald-700",
                                      diagnosticResult.recommendation === 'CAUTION' && "bg-amber-100 text-amber-700",
                                      diagnosticResult.recommendation === 'NO-GO' && "bg-rose-100 text-rose-700"
                                    )}>
                                      {diagnosticResult.recommendation} Recommendation
                                    </span>
                                  </div>
                                  <p className="font-medium text-gray-700 leading-relaxed">{diagnosticResult.reasoning}</p>
                                  <div className="grid grid-cols-2 gap-3 pt-2">
                                    <div>
                                      <p className="font-bold text-rose-700 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Identified Risks</p>
                                      <ul className="list-disc pl-4 mt-1 text-[11px] text-gray-600 space-y-1">
                                        {diagnosticResult.risks?.map((r: string, idx: number) => <li key={idx}>{r}</li>)}
                                      </ul>
                                    </div>
                                    <div>
                                      <p className="font-bold text-emerald-700 flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Strategic Advantages</p>
                                      <ul className="list-disc pl-4 mt-1 text-[11px] text-gray-600 space-y-1">
                                        {diagnosticResult.opportunities?.map((o: string, idx: number) => <li key={idx}>{o}</li>)}
                                      </ul>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {selectedEndpoint === 'generate-bid' && (
                                <div className="space-y-3">
                                  <p className="font-bold text-gray-800">Proposal Outline Generated</p>
                                  <div className="p-3 bg-white border border-gray-100 rounded-lg text-gray-600 font-mono text-[10px] whitespace-pre-wrap max-h-40 overflow-y-auto">
                                    {diagnosticResult.proposal}
                                  </div>
                                  <div>
                                    <p className="font-bold text-gray-700 text-[10px] uppercase">Compliance Checks:</p>
                                    <ul className="list-disc pl-4 mt-1 text-[11px] text-gray-600 space-y-0.5">
                                      {diagnosticResult.complianceChecklist?.map((c: string, idx: number) => <li key={idx}>{c}</li>)}
                                    </ul>
                                  </div>
                                </div>
                              )}

                              {selectedEndpoint === 'state-profile' && (
                                <div className="space-y-2">
                                  <p className="font-black text-sm text-gray-800">{diagnosticResult.state} Procurement Guidelines</p>
                                  <p className="text-gray-600">{diagnosticResult.summary}</p>
                                  <div className="grid grid-cols-2 gap-2 text-[10px] font-mono mt-2 pt-2 border-t border-gray-200">
                                    <div>Procurement Portal: <a href={diagnosticResult.procurementUrl} target="_blank" rel="noreferrer" className="text-indigo-600 underline">{diagnosticResult.procurementUrl}</a></div>
                                    <div>Registration: <a href={diagnosticResult.registrationUrl} target="_blank" rel="noreferrer" className="text-indigo-600 underline">{diagnosticResult.registrationUrl}</a></div>
                                    <div>Informal Cap: {diagnosticResult.thresholds?.informalThreshold}</div>
                                    <div>Formal Bid Cap: {diagnosticResult.thresholds?.formalThreshold}</div>
                                  </div>
                                </div>
                              )}

                              {selectedEndpoint === 'optimize' && (
                                <div className="space-y-3">
                                  <p className="font-bold text-emerald-800 text-[10px] uppercase">Optimized Proposal Copy:</p>
                                  <div className="p-3 bg-white border border-gray-100 rounded-lg text-gray-600 font-mono text-[10px] whitespace-pre-wrap">
                                    {diagnosticResult.optimizedContent}
                                  </div>
                                  <ul className="list-disc pl-4 text-[11px] text-gray-600 space-y-0.5">
                                    {diagnosticResult.improvements?.map((imp: string, i: number) => <li key={i}>{imp}</li>)}
                                  </ul>
                                </div>
                              )}

                              {selectedEndpoint === 'chat' && (
                                <p className="font-medium text-gray-700 leading-relaxed">{diagnosticResult.text}</p>
                              )}
                            </div>
                          </div>

                          {/* Raw collapsible code output */}
                          <div className="space-y-2">
                            <h5 className="text-[10px] uppercase font-bold text-gray-400">Raw JSON Playload & Response</h5>
                            <div className="bg-slate-900 rounded-xl p-4 text-gray-300 font-mono text-[10px] overflow-x-auto">
                              <p className="text-slate-500 border-b border-slate-800 pb-1.5 mb-1.5">// Outgoing API Payload</p>
                              <pre className="text-emerald-400 max-h-32 overflow-y-auto mb-4">{JSON.stringify(diagnosticPayload, null, 2)}</pre>
                              
                              <p className="text-slate-500 border-b border-slate-800 pb-1.5 mb-1.5">// Incoming JSON Response</p>
                              <pre className="text-sky-400 max-h-40 overflow-y-auto">{JSON.stringify(diagnosticResult, null, 2)}</pre>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="py-20 text-center space-y-3">
                      <Sliders className="w-12 h-12 text-gray-200 mx-auto animate-bounce" />
                      <h4 className="font-bold text-gray-800">No active test session</h4>
                      <p className="text-xs text-gray-500 max-w-sm mx-auto">
                        Alter any parameter input variables above and trigger "Execute Endpoint Test" to request full server-side validations.
                      </p>
                    </div>
                  )}
                </Card>
              </div>
            </motion.div>
          )}

          {/* ANALYTICS & LOGS TAB */}
          {activeSubTab === 'analytics' && (
            <motion.div
              key="analytics-panel"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.15 }}
              className="space-y-6"
              id="admin-analytics-container"
            >
              {/* Stats Summary row */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card className="p-6 space-y-1.5 relative overflow-hidden" id="card-stat-total-tenants">
                  <span className="text-xs text-gray-500 font-bold uppercase tracking-wider">Total Active Tenants</span>
                  <p className="text-3xl font-black text-indigo-900">{vendorsList.length}</p>
                  <p className="text-[11px] text-emerald-600 font-bold flex items-center gap-0.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Fully synced active users
                  </p>
                  <Building className="absolute -right-4 -bottom-4 w-16 h-16 text-indigo-500/5 rotate-12" />
                </Card>

                <Card className="p-6 space-y-1.5 relative overflow-hidden" id="card-stat-annual-run-rate">
                  <span className="text-xs text-gray-500 font-bold uppercase tracking-wider">Estimated Monthly Run-Rate</span>
                  <p className="text-3xl font-black text-indigo-900">
                    ${getSubRevenueStats().reduce((acc, curr) => acc + curr.revenue, 0)} / mo
                  </p>
                  <p className="text-[11px] text-indigo-600 font-bold flex items-center gap-0.5">
                    Pro & Enterprise composite licenses
                  </p>
                  <Zap className="absolute -right-4 -bottom-4 w-16 h-16 text-indigo-500/5 rotate-12" />
                </Card>

                <Card className="p-6 space-y-1.5 relative overflow-hidden" id="card-stat-gemini-utilization">
                  <span className="text-xs text-gray-500 font-bold uppercase tracking-wider">System Endpoint Health</span>
                  <p className="text-3xl font-black text-indigo-900">100%</p>
                  <p className="text-[11px] text-emerald-600 font-bold flex items-center gap-0.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> All 6 controllers online
                  </p>
                  <Activity className="absolute -right-4 -bottom-4 w-16 h-16 text-indigo-500/5 rotate-12" />
                </Card>
              </div>

              {/* Graphical Charts */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                
                {/* Plan Tier Distribution */}
                <Card className="p-6 space-y-4" id="chart-tier-distribution">
                  <h4 className="font-bold text-sm text-gray-900">User Subscription Licensing</h4>
                  <p className="text-xs text-gray-500 leading-normal">
                    Aggregate breakdown of licensed user instances mapped across the database directory.
                  </p>
                  <div className="h-64 flex items-center justify-center">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={getTierStats()}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={80}
                          paddingAngle={4}
                          dataKey="value"
                        >
                          {getTierStats().map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => [`${value} Tenants`, 'Licensed Count']} />
                        <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ fontSize: 11 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </Card>

                {/* SaaS Revenue Metrics */}
                <Card className="p-6 space-y-4" id="chart-saas-revenue">
                  <h4 className="font-bold text-sm text-gray-900">SaaS License Revenue Estimates</h4>
                  <p className="text-xs text-gray-500 leading-normal">
                    Monthly recurring revenue (MRR) distributions mapped dynamically by user tiers.
                  </p>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={getSubRevenueStats()}>
                        <XAxis dataKey="name" stroke="#94A3B8" fontSize={10} tickLine={false} />
                        <YAxis stroke="#94A3B8" fontSize={10} tickLine={false} />
                        <Tooltip formatter={(value) => [`$${value}`, 'MRR']} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Bar dataKey="revenue" fill="#4F46E5" radius={[6, 6, 0, 0]} name="Estimated Monthly Revenue ($)" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </Card>
              </div>

              {/* Live Audit Log Stream */}
              <Card className="p-6 space-y-4" id="live-audit-trail-card">
                <div className="flex justify-between items-center border-b border-gray-100 pb-3">
                  <div className="flex items-center gap-2">
                    <Sliders className="w-5 h-5 text-indigo-500" />
                    <div>
                      <h4 className="font-bold text-sm text-gray-900">System Activity Audit Trail</h4>
                      <p className="text-xs text-gray-500">Real-time chronicle of administrator interactions and server triggers.</p>
                    </div>
                  </div>
                  <button 
                    id="btn-clear-logs"
                    onClick={() => {
                      setSystemLogs([]);
                      addToast?.({ title: 'Purged Logs', message: 'Visual audit trail reset.', type: 'info' });
                    }}
                    className="text-xs font-bold text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                  >
                    Clear Feed
                  </button>
                </div>

                <div className="space-y-2.5 max-h-80 overflow-y-auto pr-1">
                  {systemLogs.length === 0 ? (
                    <p className="text-center py-8 text-xs text-gray-400">Audit trail clear. Action entries will stream here.</p>
                  ) : (
                    systemLogs.map((log) => (
                      <div 
                        key={log.id} 
                        className={cn(
                          "p-3 rounded-lg border text-xs flex justify-between items-start gap-4 transition-all leading-normal",
                          log.type === 'success' && "bg-emerald-50/40 border-emerald-100 text-emerald-950",
                          log.type === 'info' && "bg-slate-50/40 border-gray-150 text-gray-700",
                          log.type === 'warning' && "bg-amber-50/40 border-amber-100 text-amber-950",
                          log.type === 'error' && "bg-rose-50/40 border-rose-100 text-rose-950"
                        )}
                      >
                        <div className="space-y-1">
                          <p className="font-bold flex items-center gap-1">
                            <span className={cn(
                              "w-1.5 h-1.5 rounded-full shrink-0",
                              log.type === 'success' && "bg-emerald-500",
                              log.type === 'info' && "bg-gray-400",
                              log.type === 'warning' && "bg-amber-500",
                              log.type === 'error' && "bg-rose-500"
                            )} />
                            {log.action}
                          </p>
                          <p className="text-[11px] opacity-90">{log.details}</p>
                        </div>
                        <span className="text-[10px] text-gray-400 font-mono shrink-0 pt-0.5">{log.timestamp}</span>
                      </div>
                    ))
                  )}
                </div>
              </Card>
            </motion.div>
          )}

          {/* SGPVP HARVESTER TAB */}
          {activeSubTab === 'sgpvp' && (
            <motion.div
              key="sgpvp-panel"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.15 }}
            >
              <SgpvpRetrievalDashboard />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
